import React, { useState, useRef, useEffect } from "react";
import { Share2, Link2, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DECK_SHARE_MSG, waHref, redditHref, copyText } from "@/lib/share";

// "Share This Deck" control for the deck viewer toolbar. Generates a public
// read-only link on first open, then offers copy + WhatsApp / Reddit / Discord.
export default function ShareMenu({ docId, docName }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const ensureLink = async () => {
    if (url) return url;
    setBusy(true);
    try {
      const r = await api.post(`/documents/${docId}/share`);
      setUrl(r.data.url);
      return r.data.url;
    } catch {
      toast.error("Couldn't create a share link");
      return "";
    } finally { setBusy(false); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await ensureLink();
  };

  const msg = url ? DECK_SHARE_MSG(url) : "";

  const doCopy = async () => {
    const link = await ensureLink();
    if (!link) return;
    const ok = await copyText(link);
    if (ok) { setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); }
  };

  const openSocial = (href) => window.open(href, "_blank", "noopener,noreferrer");

  const discordShare = async () => {
    const link = await ensureLink();
    if (!link) return;
    const ok = await copyText(DECK_SHARE_MSG(link));
    if (ok) toast.success("Message copied — paste it into Discord");
    window.open("https://discord.com/channels/@me", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="btn-ghost inline-flex items-center gap-2" data-testid="share-deck-button">
        <Share2 className="w-4 h-4" /> Share
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 sticky-card p-4 z-50 shadow-xl" data-testid="share-menu">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.18em] text-stone-500">Share this deck</span>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700" data-testid="share-menu-close"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-stone-600 text-sm mt-2">Anyone with this link can study the deck free.</p>

          <div className="mt-3 flex items-center gap-2">
            <input
              readOnly value={busy ? "Creating link…" : url}
              className="input text-xs flex-1" data-testid="share-link-input"
              onFocus={(e) => e.target.select()}
            />
            <button onClick={doCopy} className="btn-primary px-3 py-2 inline-flex items-center gap-1 text-sm" data-testid="share-copy-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
            </button>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <button
              onClick={async () => { const l = await ensureLink(); if (l) openSocial(waHref(DECK_SHARE_MSG(l))); }}
              className="rounded-xl border border-stone-200 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
              data-testid="share-whatsapp-button">WhatsApp</button>
            <button
              onClick={discordShare}
              className="rounded-xl border border-stone-200 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 transition-colors"
              data-testid="share-discord-button">Discord</button>
            <button
              onClick={async () => { const l = await ensureLink(); if (l) openSocial(redditHref(l, `${docName || "A LoomPDF study deck"}`)); }}
              className="rounded-xl border border-stone-200 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50 transition-colors"
              data-testid="share-reddit-button">Reddit</button>
          </div>
          {msg && <p className="text-[11px] text-stone-400 mt-3 leading-snug">“{msg}”</p>}
        </div>
      )}
    </div>
  );
}
